///
/// EarthBrowser
///
/// Created by Matt Giger on 10/26/2026
/// Copyright (c) 2026 Geodata Labs
///
/// The USGS Slab2 subduction interface under a cutout footprint, from the
/// local slab2 table: a bilinear sampler over the 0.1° nodes of the zone
/// that covers the block, plus the facts the HUD states (interface depth
/// under the mainshock, its strike and dip, and where the hypocenter sits
/// relative to it).
///

import { Slab2Table, type Slab2Row } from "@/earthbrowser/dataset-distribution/earthquakes/slab2/Slab2Table"
import { fromBlockXZ, localToLatLon, type CutoutLayout } from "./cutoutLayout"

const cNodeDeg = 0.1
/** Hypocenters within this of the interface count as "on" it (location error scale) */
const cInterfaceToleranceKm = 10

export interface Slab2Facts {
	zone: string
	/** Interface depth, strike and dip at the epicenter (undefined when the slab doesn't reach it) */
	depthAtEpicenterKm?: number
	strikeDeg?: number
	dipDeg?: number
	/** Hypocenter depth minus interface depth, km (positive = below the interface) */
	hypocenterOffsetKm?: number
	relation?: "interface" | "overriding" | "slab"
	/** Interface depth range inside the footprint, km */
	minDepthKm: number
	maxDepthKm: number
	nodeCount: number
}

export class Slab2Surface {
	private readonly nodes = new Map<string, Slab2Row>()
	public readonly facts: Slab2Facts

	private constructor(rows: Slab2Row[], zone: string, mainLat: number, mainLon: number, mainDepthKm: number) {
		let minDepth = Infinity
		let maxDepth = -Infinity
		for (const r of rows) {
			if (r.zone !== zone) continue
			this.nodes.set(keyFor(r.lon, r.lat), r)
			minDepth = Math.min(minDepth, r.depth_km)
			maxDepth = Math.max(maxDepth, r.depth_km)
		}
		const at = this.sampleAll(mainLat, mainLon)
		let relation: Slab2Facts["relation"]
		let offset: number | undefined
		if (at) {
			offset = mainDepthKm - at.depth
			relation = Math.abs(offset) <= cInterfaceToleranceKm ? "interface" : offset < 0 ? "overriding" : "slab"
		}
		this.facts = {
			zone,
			depthAtEpicenterKm: at?.depth,
			strikeDeg: at?.strike,
			dipDeg: at?.dip,
			hypocenterOffsetKm: offset,
			relation,
			minDepthKm: Number.isFinite(minDepth) ? minDepth : 0,
			maxDepthKm: Number.isFinite(maxDepth) ? maxDepth : 0,
			nodeCount: this.nodes.size
		}
	}

	/** Interface under the footprint, or undefined when no Slab2 zone covers it (or the table isn't synced yet) */
	public static async load(mainLat: number, mainLon: number, mainDepthKm: number, layout: CutoutLayout): Promise<Slab2Surface | undefined> {
		let minLat = Infinity, maxLat = -Infinity, minLon = Infinity, maxLon = -Infinity
		for (const [sx, sz] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) {
			const c = fromBlockXZ(sx * layout.lengthKm / 2, sz * layout.widthKm / 2, layout.strike)
			const g = localToLatLon(c.east + layout.centerEast, c.north + layout.centerNorth, mainLat, mainLon)
			minLat = Math.min(minLat, g.lat); maxLat = Math.max(maxLat, g.lat)
			minLon = Math.min(minLon, g.lon); maxLon = Math.max(maxLon, g.lon)
		}
		const pad = cNodeDeg * 1.5
		const rows = await Slab2Table.queryBox(minLat - pad, maxLat + pad, minLon - pad, maxLon + pad)
		if (rows.length < 4) return undefined
		// Several zones can meet under one block; take the one with the most nodes
		const counts = new Map<string, number>()
		for (const r of rows) counts.set(r.zone, (counts.get(r.zone) ?? 0) + 1)
		const zone = [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0]
		return new Slab2Surface(rows, zone, mainLat, mainLon, mainDepthKm)
	}

	/** Interface depth (km) at a point, bilinear over the surrounding nodes; undefined off the slab */
	public sampleDepth(latDeg: number, lonDeg: number): number | undefined {
		return this.sampleAll(latDeg, lonDeg)?.depth
	}

	private sampleAll(latDeg: number, lonDeg: number): { depth: number; strike?: number; dip?: number } | undefined {
		const fx = lonDeg / cNodeDeg
		const fy = latDeg / cNodeDeg
		const x0 = Math.floor(fx), y0 = Math.floor(fy)
		const tx = fx - x0, ty = fy - y0
		const corners: Array<[Slab2Row | undefined, number]> = [
			[this.nodes.get(`${x0}:${y0}`), (1 - tx) * (1 - ty)],
			[this.nodes.get(`${x0 + 1}:${y0}`), tx * (1 - ty)],
			[this.nodes.get(`${x0}:${y0 + 1}`), (1 - tx) * ty],
			[this.nodes.get(`${x0 + 1}:${y0 + 1}`), tx * ty]
		]
		let w = 0, depth = 0, dip = 0, dipW = 0, sinS = 0, cosS = 0
		for (const [node, weight] of corners) {
			if (!node || weight <= 0) continue
			w += weight
			depth += node.depth_km * weight
			if (node.dip_deg !== null) { dip += node.dip_deg * weight; dipW += weight }
			if (node.strike_deg !== null) {
				sinS += Math.sin(node.strike_deg * Math.PI / 180) * weight
				cosS += Math.cos(node.strike_deg * Math.PI / 180) * weight
			}
		}
		// Need most of the cell covered; a lone corner is the slab edge fraying
		if (w < 0.5) return undefined
		const strike = (sinS !== 0 || cosS !== 0) ? ((Math.atan2(sinS, cosS) * 180 / Math.PI) + 360) % 360 : undefined
		return { depth: depth / w, dip: dipW > 0 ? dip / dipW : undefined, strike }
	}
}

function keyFor(lon: number, lat: number): string {
	return `${Math.round(lon / cNodeDeg)}:${Math.round(lat / cNodeDeg)}`
}
